import Lang from "@/lang";
import { otherValues } from "@/settings/constants";
import { ChartDataRecord } from "@/types/charts";
import { InputDictionary } from "@/types/common";

const normalizeOther = (value: string): string => (otherValues.includes(value) ? (Lang.other as string) : value);

export const createMetricsValues = (
  mappedData: ChartDataRecord[],
  data: ChartDataRecord[],
  firstDimension: string,
  secondDimension: string,
  displayMetric: string,
  names: string[]
): void => {
  names.forEach((name: string, index: number): void => {
    mappedData[index] = {
      name: name,
    };
    data
      .filter((item: ChartDataRecord) => normalizeOther(item[firstDimension] as string) === name)
      .sort((a, b) => (b[displayMetric] as number) - (a[displayMetric] as number))
      .forEach((el: ChartDataRecord) => {
        const key = normalizeOther(el[secondDimension] as string);
        const prev = Number(mappedData[index][key] ?? 0);

        mappedData[index] = {
          ...mappedData[index],
          [key]: prev + Number(el[displayMetric] ?? 0),
        };
      });
  });
};

export const createDimensionNames = (data: ChartDataRecord[], dimension: string, resultData: string[]): void => {
  data.forEach((item: ChartDataRecord): void => {
    const metric = otherValues.includes(item[dimension] as string) ? Lang.other : item[dimension];
    if (!resultData.includes(metric as string)) {
      metric === null ? resultData.push(Lang.null) : resultData.push(metric as string);
    }
  });
  resultData.sort();
};

export const createStackedDimensionNames = (
  data: ChartDataRecord[],
  dimension: string,
  resultData: unknown[]
): void => {
  data.forEach((item: ChartDataRecord): void => {
    resultData.push({ [dimension]: item[dimension] });
  });
  resultData.sort();
};

export const sortByNameAndOrder = (a: InputDictionary, b: InputDictionary): number => {
  return (a.table?.order || Infinity) - (b.table?.order || Infinity) || a.title.localeCompare(b.title);
};

export const parseDateByMetric = (date: string | number | Date, metric: string): Date => {
  try {
    if (typeof date === "number") {
      return new Date(date);
    }

    let year: number, month: number, day: number;
    if (typeof date === "string") {
      const [y, m, d] = date.split("-").map(Number);
      if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
        console.error(`Invalid date parts in string: ${date}`);
      }
      year = y;
      month = m - 1;
      day = d;
    } else {
      const d = date instanceof Date ? date : new Date(date);
      if (isNaN(d.getTime())) console.error(`Invalid date object: ${date}`);
      year = d.getUTCFullYear();
      month = d.getUTCMonth();
      day = d.getUTCDate();
    }

    switch (metric) {
      case "quarter":
        return new Date(Date.UTC(year, Math.floor(month / 3) * 3, 1, 12));
      case "month":
        return new Date(Date.UTC(year, month, 1, 12));
      case "day":
      case "week":
        return new Date(Date.UTC(year, month, day, 12));
      default:
        return new Date(Date.UTC(year, month, 1, 12));
    }
  } catch (error) {
    console.error(`Error parsing date ${date}:`, error);
    return new Date(NaN);
  }
};

export const parseDateByMetricWithMach = (date: string | number | Date, metric: string): Date => {
  let year: number, month: number, day: number;

  try {
    if (typeof date === "number") {
      const d = new Date(date);
      if (isNaN(d.getTime())) throw new Error(`Invalid timestamp: ${date}`);
      year = d.getUTCFullYear();
      month = d.getUTCMonth();
      day = d.getUTCDate();
    } else if (typeof date === "string") {
      const parts = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!parts) throw new Error(`Invalid date string format: ${date}`);
      year = parseInt(parts[1], 10);
      month = parseInt(parts[2], 10) - 1;
      day = parseInt(parts[3], 10);
    } else {
      if (!(date instanceof Date) || isNaN(date.getTime())) throw new Error(`Invalid date object: ${date}`);
      year = date.getUTCFullYear();
      month = date.getUTCMonth();
      day = date.getUTCDate();
    }

    let result: Date;
    switch (metric) {
      case "quarter":
        result = new Date(Date.UTC(year, Math.floor(month / 3) * 3, 1, 12));
        break;
      case "month":
        result = new Date(Date.UTC(year, month, 1, 12));
        break;
      case "day":
      case "week":
        result = new Date(Date.UTC(year, month, day, 12));
        break;
      default:
        result = new Date(Date.UTC(year, month, 1, 12));
    }
    return result;
  } catch (error) {
    return new Date(NaN);
  }
};
